'use client';

import cx from 'classix';
import { useTranslations } from 'next-intl';
import { MdOutlineOpenInNew } from 'react-icons/md';

import ListingAddress from '@/features/listing/components/listing-card/listing-address/listing-address';
import { type ListingCardViewBaseProps } from '@/features/listing/components/listing-card/listing-card-types';
import ListingCarousel from '@/features/listing/components/listing-card/listing-carousel/listing-carousel';
import ListingMessageButton from '@/features/listing/components/listing-card/listing-message/listing-message-button';
import ListingMoveInDate from '@/features/listing/components/listing-card/listing-move-in-date/listing-move-in-date';
import ListingRate from '@/features/listing/components/listing-card/listing-rate/listing-rate';
import ListingUser from '@/features/listing/components/listing-card/listing-user/listing-user';
import ListingVerifications
  from '@/features/listing/components/listing-card/listing-verifications/listing-verifications';
import { VerificationStatus } from '@/features/user/types/user-id-verification-types';
import { Link } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Badge from '@/ui/badge/badge';

import styles from './listing-card-view-grid.module.scss';

export default function ListingCardViewGrid(props: ListingCardViewBaseProps) {
  const {
    isListingLinkDisabled,
    isListingPerson,
    link = null,
    listing,
    messageSrc,
    showCheckStatus,
    showDescription = true,
    showEditButton = false,
    showListingUser = true,
    showMessageButton = true,
    showMissingData = false,
    showMoveInDate = true,
    showVerifications = true,
  } = props;

  const listingStatusToBadgeColor = {
    [VerificationStatus.New]: 'gray',
    [VerificationStatus.Passed]: 'green',
    [VerificationStatus.Pending]: 'orange',
    [VerificationStatus.Rejected]: 'red',
    [VerificationStatus.Error]: 'red',
  } as const;

  const translatesListingCard = useTranslations('listing.card');
  const translatesVerifications = useTranslations('global.verifications');
  const translatesActions = useTranslations('global.actions');

  const renderListingCarousel = () => (
    <>
      {showEditButton && (
        <div className={styles.listingCardViewGrid_editButton}>
          {translatesActions('edit')}
        </div>
      )}

      <ListingCarousel
        listingRefreshedDate={listing.refreshed}
        images={listing.images}
        imagesAlt={listing.title}
        isNew={listing.isNew}
        isBookmarked={listing.isBookmarked}
        listingId={listing.id}
        listingTypeId={listing.listingTypeId}
      />
    </>
  );

  const renderListingInfo = () => (
    <>
      <div className={styles.listingCardViewGrid_badges}>
        {(showMissingData && listing.isDataMissing) && (
          <Badge>{translatesListingCard('complete-listing')}</Badge>
        )}

        {showCheckStatus && (
          <Badge
            color={listingStatusToBadgeColor[listing.addressVerificationStatus]}
          >
            {translatesVerifications(`address-check-${listing.addressVerificationStatus}`)}
          </Badge>
        )}
      </div>

      <ListingAddress address={listing.addressFormatted} listingTypeId={listing.listingTypeId} />

      <h2 className={styles.listingCardViewGrid_title}>
        {listing.title}
      </h2>

      {showDescription && (
        <div className={styles.listingCardViewGrid_description}>
          <p dangerouslySetInnerHTML={{ __html: listing.description.replaceAll(/\r\n|\r|\n/g, '') }} />
          <MdOutlineOpenInNew className={styles.listingCardViewGrid_descriptionIcon} />
        </div>
      )}

      {showMoveInDate && (
        <ListingMoveInDate moveInDate={listing.calendar.dateIn} isListingPerson={isListingPerson} />
      )}

      <ListingRate rate={listing.rates} />

      {showVerifications && (
        <ListingVerifications
          isAddressVerified={listing.addressVerificationStatus}
          userVerifications={listing.user.verifications}
        />
      )}
    </>
  );

  return (
    <div
      className={cx(
        styles.listingCardViewGrid,
        !isListingPerson && showCheckStatus && styles[`listingCardViewGrid__${listing.addressVerificationStatus}Status`],
        isListingPerson && showCheckStatus && styles.listingCardViewGrid__personListing,
      )}
    >
      {isListingLinkDisabled ? (
        <div>
          {renderListingCarousel()}
        </div>
      ) : (
        // eslint-disable-next-line canonical/prefer-react-lazy
        <Link
          href={link ?? `${ROUTER_LINKS.listings}/${String(listing.id)}`}
        >
          {renderListingCarousel()}
        </Link>
      )}

      <div className={styles.listingCardViewGrid_body}>
        {showListingUser && (
          <Link
            className={styles.listingCardViewGrid_user}
            href={`${ROUTER_LINKS.profile}/${String(listing.user.id)}`}
          >
            <ListingUser user={listing.user} />
          </Link>
        )}

        {isListingLinkDisabled ? (
          <div className={styles.listingCardViewGrid_info}>
            {renderListingInfo()}
          </div>
        ) : (
          // eslint-disable-next-line canonical/prefer-react-lazy
          <Link
            className={styles.listingCardViewGrid_info}
            href={link ?? `${ROUTER_LINKS.listings}/${String(listing.id)}`}
          >
            {renderListingInfo()}
          </Link>
        )}

        {showMessageButton && (
          <div className={styles.listingCardViewGrid_message}>
            <ListingMessageButton listing={listing} messageSrc={messageSrc} />
          </div>
        )}
      </div>
    </div>
  );
}
